import { Link } from "react-router-dom";
import { BookOpen, CircleCheck, CircleDashed, KeyRound } from "lucide-react";
import { fmtDateTime } from "@/lib/api";
import { Chip } from "@/components/common";

const UNLINKED = ["not_connected", "configuration_required", "planned"];

function Stat({ icon: Icon, label, value, tone, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-3">
      <Icon size={16} className={`${tone} shrink-0 hidden sm:block`} />
      <div><div className="font-display text-xl text-[#F7FAFF] leading-none">{value}</div><div className="text-[11px] text-[#7C8BAD] mt-1">{label}</div></div>
    </div>
  );
}

function AccountRow({ c, a }) {
  const ok = c.status === "connected" && c.token_status === "valid" && !c.missing_permissions.length;
  return (
    <tr data-testid={`summary-account-${c.key}-${a.id}`} className="border-t border-[#172342]">
      <td className="py-2 pr-3 text-[#F7FAFF] truncate max-w-[140px] sm:max-w-[220px]">{a.name}<div className="sm:hidden text-[11px] text-[#7C8BAD]">{c.name}</div></td>
      <td className="py-2 pr-3 text-[#AAB6CC] hidden sm:table-cell">{c.name}</td>
      <td className="py-2 pr-3"><Chip tone={ok ? "green" : "amber"}>{ok ? "Operational" : "Needs attention"}</Chip></td>
      <td className="py-2 text-[#7C8BAD] whitespace-nowrap">{c.last_sync_at ? fmtDateTime(c.last_sync_at) : "Never"}</td>
    </tr>
  );
}

export function ConnectedAccountsSummary({ channels, canManage }) {
  const live = channels.filter((c) => c.live_connector);
  const linked = live.filter((c) => !UNLINKED.includes(c.status));
  const rows = linked.flatMap((c) => c.available_assets.filter((a) => c.selected_asset_ids.includes(a.id)).map((a) => ({ c, a })));
  const unassigned = linked.filter((c) => !c.selected_asset_ids.length);
  const needsConfig = [...new Set(live.filter((c) => !c.configured).map((c) => c.family_name))];
  return (
    <section data-testid="connected-accounts-summary" className="cq-card p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg text-[#F7FAFF]">Connected accounts</h2>
        <span className="flex-1" />
        {canManage && <Link to="/channels/setup" data-testid="channel-setup-link" className="cq-btn cq-btn-ghost !py-1.5 !text-xs"><BookOpen size={13} /> Setup & approval guide</Link>}
      </div>
      <div className="grid grid-cols-3 gap-4 sm:flex sm:flex-wrap sm:gap-8">
        <Stat testId="summary-operational-count" icon={CircleCheck} tone="text-[#4BE3A4]" label="Accounts in use" value={rows.length} />
        <Stat testId="summary-unassigned-count" icon={CircleDashed} tone="text-[#F4B860]" label="Connected, no account chosen" value={unassigned.length} />
        <Stat testId="summary-config-count" icon={KeyRound} tone="text-[#F4B860]" label="Platforms awaiting credentials" value={needsConfig.length} />
      </div>
      {rows.length ? (
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="summary-accounts-table">
          <thead><tr className="text-left text-[11px] text-[#7C8BAD]"><th className="pb-1 font-normal">Account</th><th className="pb-1 font-normal hidden sm:table-cell">Channel</th><th className="pb-1 font-normal">Status</th><th className="pb-1 font-normal">Last verified</th></tr></thead>
          <tbody>{rows.map(({ c, a }) => <AccountRow key={`${c.key}-${a.id}`} c={c} a={a} />)}</tbody>
        </table></div>
      ) : <p data-testid="summary-empty" className="text-xs text-[#7C8BAD]">No pages or ad accounts are in use by this workspace yet. Accounts appear here once a connection is tested and you've chosen which accounts belong to this workspace.</p>}
      {needsConfig.length > 0 && <p data-testid="summary-config-note" className="text-xs text-[#F4B860]">Awaiting app credentials: {needsConfig.join(", ")}. These can't connect yet.</p>}
    </section>
  );
}
